import { PageLoadError } from '@components/page-load-error';
import {
  callCobrandErrorToast,
  getCobrandQueryOptions,
} from '@features/navigation/api/get-cobrand';
import { getProfileQueryOptions } from '@features/user/api/get-profile';
import { GetProfileResponse, UserState } from '@features/user/types/get-profile-response';
import Box from '@mui/material/Box';
import { getBetaFeaturesQueryOptions } from '@src/api/beta-features/get-beta-features';
import Authenticated from '@src/routes/-layouts/authenticated-layout';
import { CobrandImage } from '@src/types/get-cobrand-response';
import { createFileRoute } from '@tanstack/react-router';
import { getCookie, hasAirMaxEnabled, hasAirMaxOptInEnabled  } from '@utils';
import { AxiosError } from 'axios';

type AuthenticatedRouteData = {
  profile: GetProfileResponse;
  cobrandImage?: CobrandImage;
  airMaxOptInEnabled: boolean;
};

export const Route = createFileRoute('/_authenticated')({
  component: Authenticated,
  errorComponent: ({ error }) => (
    <Box
      height='100vh'
      sx={(theme) => ({ background: theme.customGradients.primary })}
    >
      <PageLoadError pageName='Max' error={error} />
    </Box>
  ),
  beforeLoad: async ({ context }) => {
    const data = await context.queryClient.fetchQuery(
      getBetaFeaturesQueryOptions,
    );
    void hasAirMaxEnabled(data);
  },
  loader: async ({ context }): Promise<AuthenticatedRouteData> => {
    const partner = getCookie('partner');
    const [profileResult, cobrandResult] = await Promise.allSettled([
      context.queryClient.fetchQuery(getProfileQueryOptions),
      context.queryClient.fetchQuery(getCobrandQueryOptions({ partner })),
    ]);

    let profile: GetProfileResponse | undefined;
    if (profileResult.status === 'fulfilled') {
      profile = profileResult.value;
    } else {
      throw new AxiosError('Failed to load user profile. Please log in again.');
    }

    let cobrandImage: CobrandImage | undefined;
    if (cobrandResult.status === 'fulfilled') {
      cobrandImage = cobrandResult.value?.image;
    } else {
      callCobrandErrorToast();
      console.error(cobrandResult.reason);
    }

    if (profile && profile.state !== UserState.Approved) {
      window.location.replace('/account-under-review');
    }

    const betaFeatureData = await context.queryClient.fetchQuery(
      getBetaFeaturesQueryOptions,
    );

    const airMaxOptInEnabled = hasAirMaxOptInEnabled(betaFeatureData);
    return {
      profile,
      cobrandImage,
      airMaxOptInEnabled,
    };
  },
});
